import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  ArrowLeft,
  Loader2,
  Wallet,
  Building2,
  UserCircle2,
  Plus,
  Trash2,
  Check,
  X,
  IndianRupee,
  Coins,
  Inbox,
  Download,
  FileDown,
  Key,
  Scale,
  Upload,
  Send,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { LedgerAccessTab } from "@/components/LedgerAccessTab";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  buildStaffLedgerCSV,
  buildAgencyLedgerCSV,
  downloadCSV,
  downloadLedgerPDF,
  AggregatedLedgerTable,
  DateRangeChips,
} from "@/pages/MyLedger";

const OP_LABEL = { INDANE: "Indane", BHARAT_GAS: "BharatGas", HP_GAS: "HP Gas" };
void OP_LABEL; // reserved for legacy exports; keep symbol so imports don't break.

// IST helpers
const IST_OPTS = { timeZone: "Asia/Kolkata" };
function fmtDate(yyyymmdd) {
  if (!yyyymmdd) return "";
  const [y, m, d] = yyyymmdd.split("-");
  try {
    const dt = new Date(Date.UTC(+y, +m - 1, +d));
    return dt.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...IST_OPTS,
    });
  } catch {
    return yyyymmdd;
  }
}
function todayIST() {
  return new Date().toLocaleDateString("en-CA", { ...IST_OPTS });
}
const rupee = (n) =>
  "₹" +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------
function SectionCard({ title, icon: Icon, children, right }) {
  return (
    <section className="bg-white border border-[#EDEDE5] rounded-lg overflow-hidden">
      <header className="flex items-center justify-between px-4 py-3 border-b border-[#F0F0EA] bg-[#F8F8F3]">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#385C4B]" />}
          <h2
            className="font-semibold text-[#1C1C19] text-sm uppercase tracking-wider"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {title}
          </h2>
        </div>
        {right}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function BalancePill({ amount }) {
  const positive = amount > 0;
  const zero = Math.abs(amount) < 0.005;
  const color = zero
    ? "bg-[#F0F0EA] text-[#6B6B61]"
    : positive
    ? "bg-red-50 text-red-700 border border-red-200"
    : "bg-emerald-50 text-emerald-700 border border-emerald-200";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-xs ${color}`}
    >
      {rupee(Math.abs(amount))} {zero ? "" : positive ? "DR" : "CR"}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Rate Master tab
// -----------------------------------------------------------------------------
function RateMasterTab({ agencies, staffList, agencyRates, staffRates, reload }) {
  // Latest-per-ref lookup for quick display
  const latestByAgency = useMemo(() => {
    const m = {};
    for (const r of agencyRates) {
      if (!m[r.ref_id] || m[r.ref_id].effective_from < r.effective_from) m[r.ref_id] = r;
    }
    return m;
  }, [agencyRates]);
  const latestByStaff = useMemo(() => {
    // Key = `${ref_id}||${operator}` (operator "" = default row).
    // Latest effective_from wins for each key.
    const m = {};
    for (const r of staffRates) {
      const op = (r.operator || "").toUpperCase();
      const k = `${r.ref_id}||${op}`;
      if (!m[k] || m[k].effective_from < r.effective_from) m[k] = { ...r, operator: op };
    }
    // Group into staff → [rows]
    const grouped = {};
    for (const r of Object.values(m)) {
      grouped[r.ref_id] = grouped[r.ref_id] || [];
      grouped[r.ref_id].push(r);
    }
    return grouped;
  }, [staffRates]);

  const [aForm, setAForm] = useState({
    ref_id: "",
    rate: "",
    effective_from: todayIST(),
    note: "",
  });
  const [sForm, setSForm] = useState({
    ref_id: "",
    rate: "",
    operator: "",
    effective_from: todayIST(),
    note: "",
  });

  const submitAgency = async () => {
    if (!aForm.ref_id || !aForm.rate) {
      toast.error("Agency + rate required");
      return;
    }
    try {
      await api.post("/wallet/rates/agency", {
        ref_id: aForm.ref_id,
        rate: parseFloat(aForm.rate),
        effective_from: aForm.effective_from,
        note: aForm.note,
      });
      toast.success("Rate saved");
      setAForm({ ref_id: "", rate: "", effective_from: todayIST(), note: "" });
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const submitStaff = async () => {
    if (!sForm.ref_id || !sForm.rate) {
      toast.error("Staff + rate required");
      return;
    }
    try {
      await api.post("/wallet/rates/staff", {
        ref_id: sForm.ref_id,
        rate: parseFloat(sForm.rate),
        operator: sForm.operator || "",
        effective_from: sForm.effective_from,
        note: sForm.note,
      });
      toast.success("Rate saved");
      setSForm({ ref_id: "", rate: "", operator: "", effective_from: todayIST(), note: "" });
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const deleteRate = async (kind, id) => {
    if (!window.confirm("Delete this rate version?")) return;
    try {
      await api.delete(`/wallet/rates/${kind}/${id}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Iter89: one-click legacy dedupe. Collapses every duplicate rate row
  // (same ref_id + effective_from) down to the newest-created one so the
  // resolver has an unambiguous choice. Safe to re-run.
  const cleanupDuplicates = async () => {
    if (!window.confirm(
      "Purani duplicate rate entries ko clean karein? Har (agency/staff · date) ke liye latest-saved row hi rahegi, baaki delete ho jayengi. Safe hai — dobara chala sakte ho.",
    )) return;
    try {
      const { data } = await api.post("/wallet/rates/cleanup-duplicates");
      const a = data.report?.agency || {};
      const s = data.report?.staff || {};
      toast.success(
        `Cleanup done — Agency: ${a.deleted || 0} deleted, ${a.kept || 0} kept · Staff: ${s.deleted || 0} deleted, ${s.kept || 0} kept`,
      );
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div
        className="flex items-center justify-between gap-3 flex-wrap bg-[#FFF8E7] border border-[#D9C89E] rounded-md px-3 py-2"
        data-testid="rates-cleanup-strip"
      >
        <div className="text-xs text-[#6B5A2E] leading-snug">
          <b>Duplicate rates safai:</b> Agar same (agency, date) ya (staff, operator, date)
          par pehle multiple entries save ho gayi hain, ye button unko collapse karke sirf
          latest-saved row rakhega. Aaj se naye saves auto-upsert honge.
        </div>
        <button
          onClick={cleanupDuplicates}
          className="px-3 py-1.5 text-xs font-semibold bg-[#7A5A1F] text-white rounded hover:bg-[#5F4718] whitespace-nowrap"
          data-testid="rates-cleanup-btn"
        >
          Cleanup Duplicates
        </button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
      {/* Agency rates */}
      <SectionCard title="Agency Rate Master" icon={Building2}>
        <div
          className="flex flex-wrap gap-2 mb-3"
          data-testid="agency-rate-form"
        >
          <select
            value={aForm.ref_id}
            onChange={(e) => setAForm({ ...aForm, ref_id: e.target.value })}
            className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded bg-white flex-1 min-w-[180px]"
            data-testid="agency-rate-select"
          >
            <option value="">— pick agency —</option>
            {[...agencies]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
          </select>
          <input
            type="number"
            step="0.01"
            placeholder="₹ / unit"
            value={aForm.rate}
            onChange={(e) => setAForm({ ...aForm, rate: e.target.value })}
            className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded font-mono w-[110px]"
            data-testid="agency-rate-amount"
          />
          <input
            type="date"
            value={aForm.effective_from}
            onChange={(e) => setAForm({ ...aForm, effective_from: e.target.value })}
            className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded w-[140px]"
            data-testid="agency-rate-date"
          />
          <button
            onClick={submitAgency}
            className="px-3 py-1.5 bg-[#385C4B] text-white rounded text-sm hover:bg-[#2D4A3C] flex items-center gap-1 whitespace-nowrap"
            data-testid="agency-rate-save"
          >
            <Plus className="w-3 h-3" /> Save
          </button>
        </div>
        <input
          type="text"
          placeholder="Note (optional — e.g. 'Q3 revision')"
          value={aForm.note}
          onChange={(e) => setAForm({ ...aForm, note: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-[#DCDCD3] rounded mb-4"
        />

        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-[#6B6B61] bg-[#F5F5F0]">
            <tr>
              <th className="text-left px-2 py-2">Agency</th>
              <th className="text-right px-2 py-2">Current ₹</th>
              <th className="text-left px-2 py-2">Since</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {agencies.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-4 text-[#6B6B61]">
                  No agencies configured.
                </td>
              </tr>
            )}
            {[...agencies]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((g) => {
                const r = latestByAgency[g.id];
                return (
                  <tr
                    key={g.id}
                    className="border-t border-[#F0F0EA]"
                    data-testid={`agency-rate-row-${g.id}`}
                  >
                    <td className="px-2 py-2">{g.name}</td>
                    <td className="px-2 py-2 text-right font-mono font-semibold">
                      {r ? rupee(r.rate) : <span className="text-red-500">— not set —</span>}
                    </td>
                    <td className="px-2 py-2 text-xs text-[#6B6B61]">
                      {r ? fmtDate(r.effective_from) : "—"}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r && (
                        <button
                          onClick={() => deleteRate("agency", r.id)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete latest rate version"
                          data-testid={`agency-rate-del-${g.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        {agencyRates.length > agencies.length && (
          <details className="mt-3 text-xs text-[#6B6B61]">
            <summary className="cursor-pointer">
              Full rate history ({agencyRates.length} versions)
            </summary>
            <table className="w-full mt-2 text-xs">
              <thead className="text-[10px] uppercase text-[#6B6B61] bg-[#F5F5F0]">
                <tr>
                  <th className="text-left px-2 py-1">Agency</th>
                  <th className="text-right px-2 py-1">Rate</th>
                  <th className="text-left px-2 py-1">Since</th>
                  <th className="text-left px-2 py-1">Note</th>
                </tr>
              </thead>
              <tbody>
                {agencyRates.map((r) => (
                  <tr key={r.id} className="border-t border-[#F0F0EA]">
                    <td className="px-2 py-1">{r.agency_name}</td>
                    <td className="px-2 py-1 text-right font-mono">{rupee(r.rate)}</td>
                    <td className="px-2 py-1">{fmtDate(r.effective_from)}</td>
                    <td className="px-2 py-1 truncate max-w-[180px]" title={r.note}>
                      {r.note || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </SectionCard>

      {/* Staff rates */}
      <SectionCard title="Staff Rate Master" icon={UserCircle2}>
        <div className="text-[11px] text-[#6B6B61] mb-2">
          Operator blank rakhne pe <b>default rate</b> lagega (jab specific
          Bharat/HP/Indane rate na ho). Har operator ke liye alag rate save karke
          override kar sakte ho.
        </div>
        <div
          className="flex flex-wrap gap-2 mb-3"
          data-testid="staff-rate-form"
        >
          <input
            list="staff-names"
            placeholder="Staff name (case-insensitive)"
            value={sForm.ref_id}
            onChange={(e) => setSForm({ ...sForm, ref_id: e.target.value })}
            className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded flex-1 min-w-[160px]"
            data-testid="staff-rate-name"
          />
          <datalist id="staff-names">
            {staffList.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
          <select
            value={sForm.operator}
            onChange={(e) => setSForm({ ...sForm, operator: e.target.value })}
            className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded bg-white w-[130px]"
            data-testid="staff-rate-operator"
            title="Operator (blank = default rate for all operators)"
          >
            <option value="">Default (all)</option>
            <option value="INDANE">Indane</option>
            <option value="BHARAT_GAS">Bharat Gas</option>
            <option value="HP_GAS">HP Gas</option>
          </select>
          <input
            type="number"
            step="0.01"
            placeholder="₹ / unit"
            value={sForm.rate}
            onChange={(e) => setSForm({ ...sForm, rate: e.target.value })}
            className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded font-mono w-[110px]"
            data-testid="staff-rate-amount"
          />
          <input
            type="date"
            value={sForm.effective_from}
            onChange={(e) => setSForm({ ...sForm, effective_from: e.target.value })}
            className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded w-[140px]"
            data-testid="staff-rate-date"
          />
          <button
            onClick={submitStaff}
            className="px-3 py-1.5 bg-[#385C4B] text-white rounded text-sm hover:bg-[#2D4A3C] flex items-center gap-1 whitespace-nowrap"
            data-testid="staff-rate-save"
          >
            <Plus className="w-3 h-3" /> Save
          </button>
        </div>
        <input
          type="text"
          placeholder="Note (optional)"
          value={sForm.note}
          onChange={(e) => setSForm({ ...sForm, note: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-[#DCDCD3] rounded mb-4"
        />

        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-[#6B6B61] bg-[#F5F5F0]">
            <tr>
              <th className="text-left px-2 py-2">Staff</th>
              <th className="text-left px-2 py-2">Operator</th>
              <th className="text-right px-2 py-2">Current ₹</th>
              <th className="text-left px-2 py-2">Since</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {staffList.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-4 text-[#6B6B61]">
                  No staff detected yet. Names populate as team members upload.
                </td>
              </tr>
            )}
            {staffList.flatMap((name) => {
              const rows = latestByStaff[name] || [];
              if (rows.length === 0) {
                // Show "not set" placeholder row so user can spot missing staff
                return [
                  <tr
                    key={name}
                    className="border-t border-[#F0F0EA]"
                    data-testid={`staff-rate-row-${name}`}
                  >
                    <td className="px-2 py-2">{name}</td>
                    <td className="px-2 py-2 text-xs text-[#6B6B61]">Default</td>
                    <td className="px-2 py-2 text-right font-mono font-semibold">
                      <span className="text-red-500">— not set —</span>
                    </td>
                    <td className="px-2 py-2 text-xs text-[#6B6B61]">—</td>
                    <td className="px-2 py-2" />
                  </tr>,
                ];
              }
              // Sort within staff: default first, then operator alpha
              const sorted = [...rows].sort((a, b) => {
                if ((a.operator || "") === "") return -1;
                if ((b.operator || "") === "") return 1;
                return a.operator.localeCompare(b.operator);
              });
              return sorted.map((r, i) => (
                <tr
                  key={`${name}-${r.operator || "default"}`}
                  className="border-t border-[#F0F0EA]"
                  data-testid={`staff-rate-row-${name}-${r.operator || "default"}`}
                >
                  <td className="px-2 py-2">{i === 0 ? name : ""}</td>
                  <td className="px-2 py-2 text-xs">
                    {r.operator ? (
                      <span className="px-1.5 py-0.5 rounded bg-[#385C4B]/10 text-[#385C4B] uppercase tracking-wider text-[10px]">
                        {r.operator.replace("_", " ")}
                      </span>
                    ) : (
                      <span className="text-[#6B6B61]">Default</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right font-mono font-semibold">
                    {rupee(r.rate)}
                  </td>
                  <td className="px-2 py-2 text-xs text-[#6B6B61]">
                    {fmtDate(r.effective_from)}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button
                      onClick={() => deleteRate("staff", r.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Delete latest rate version"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      </SectionCard>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Ledger drawer — shows a running ledger for either an agency or a staff
// -----------------------------------------------------------------------------
function LedgerView({ scope, refId, refName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState({ from: "2020-01-01", to: todayIST() });
  // Iter80: user-controllable sort — "desc" (newest first, default) or
  // "asc" (bank-passbook oldest-first). Flows through to UI table + PDF
  // + CSV exports so downloads mirror the on-screen order.
  const [sortOrder, setSortOrder] = useState("desc");
  // Reconciliation-with-staff-Excel state — see the "⚖ Reconcile" button
  // in the ledger header and the modal at the bottom of this component.
  const [reconOpen, setReconOpen] = useState(false);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconResult, setReconResult] = useState(null);
  const [reconFile, setReconFile] = useState(null);
  const [entry, setEntry] = useState({
    kind: "payment",
    amount: "",
    date: todayIST(),
    note: "",
  });
  // Preset-reason helper (only shown for adjustment kinds) — lets admins
  // pick a canonical reason like "IVRS booking" or "WhatsApp/Excel sent"
  // and auto-computes the amount from qty × rate so the ledger entry
  // shows exactly WHY it was added. Requested by user (Feb 2026):
  // "RATE QUANTITY AAYE...KIS AMOUNT ME KYA ENTRY HO RHA HAI...LEDGER
  // ME CHANGE HONE KA REASON AA JAYEGA...FULL TRANSPARENCY".
  const [preset, setPreset] = useState({ reason: "", qty: "", rate: "" });
  const PRESET_REASONS = [
    { key: "ivrs", label: "Only booking via IVRS" },
    { key: "whatsapp_excel", label: "Details on WhatsApp — Excel adjustment" },
    { key: "manually", label: "Manually" },
  ];
  const applyPreset = (nextPreset) => {
    setPreset(nextPreset);
    const q = parseFloat(nextPreset.qty || "0");
    const r = parseFloat(nextPreset.rate || "0");
    const computed = q > 0 && r > 0 ? +(q * r).toFixed(2) : "";
    // Auto-fill amount + prefix note with the reason for full transparency.
    // Ledger rules unchanged — kind/amount fields still drive the balance;
    // this just adds a reason+math trail into the Note.
    const reasonObj = PRESET_REASONS.find((p) => p.key === nextPreset.reason);
    const reasonLabel = reasonObj?.label || "";
    let note = entry.note || "";
    if (reasonLabel && !note.startsWith(reasonLabel)) {
      // Strip any prior preset prefix so switching reasons stays clean.
      for (const p of PRESET_REASONS) {
        if (note.startsWith(p.label)) {
          note = note.slice(p.label.length).replace(/^[ ·—-]*/, "");
          break;
        }
      }
      const mathSuffix = q > 0 && r > 0 ? ` · Qty ${q} × ₹${r} = ₹${computed}` : "";
      note = `${reasonLabel}${mathSuffix}${note ? " · " + note : ""}`;
    }
    setEntry((prev) => ({
      ...prev,
      amount: computed !== "" ? String(computed) : prev.amount,
      note,
    }));
  };

  // Google Sheet push (Feb 2026, staff scope only): each staff can have
  // their OWN Apps Script webhook URL saved by admin. Push button fires
  // the current-window ledger over to that sheet. Ashish/Komal ka mix
  // nahi hoga — completely separate destinations.
  const [sheetHookOpen, setSheetHookOpen] = useState(false);
  const [sheetHookUrl, setSheetHookUrl] = useState("");
  const [sheetHookLoading, setSheetHookLoading] = useState(false);
  const [sheetHookSaving, setSheetHookSaving] = useState(false);
  const [sheetHookPushing, setSheetHookPushing] = useState(false);
  const [sheetHookMeta, setSheetHookMeta] = useState({ updated_at: "", updated_by: "" });
  const APPS_SCRIPT_SNIPPET = `function doPost(e) {
  const sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  const body = JSON.parse(e.postData.contents);
  sheet.clearContents();
  sheet.appendRow(['Date','Agency','Operator','LPG ID','DAC','Qty','Rate','Debit','Credit','Kind','Note']);
  (body.rows || []).forEach(function(r) {
    sheet.appendRow([
      r.date || '', r.agency || '', r.operator || '',
      r.lpg_id || '', r.dac_code || '',
      Number(r.qty) || 0, Number(r.rate) || 0,
      Number(r.debit) || 0, Number(r.credit) || 0,
      r.kind || '', r.description || r.note || ''
    ]);
  });
  const t = body.totals || {};
  sheet.appendRow([]);
  sheet.appendRow(['TOTALS','','','','','','',
    t.opening || 0, t.payouts || 0, 'balance=' + (t.balance || 0), '']);
  return ContentService
    .createTextOutput(JSON.stringify({ok:true, count:(body.rows||[]).length}))
    .setMimeType(ContentService.MimeType.JSON);
}`;
  const openSheetHookPanel = async () => {
    setSheetHookOpen(true);
    setSheetHookLoading(true);
    try {
      const { data: res } = await api.get(
        `/wallet/ledger/staff/${encodeURIComponent(refName)}/sheet-webhook`
      );
      setSheetHookUrl(res.webhook_url || "");
      setSheetHookMeta({
        updated_at: res.updated_at || "",
        updated_by: res.updated_by || "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSheetHookLoading(false);
    }
  };
  const saveSheetHook = async () => {
    setSheetHookSaving(true);
    try {
      await api.put(
        `/wallet/ledger/staff/${encodeURIComponent(refName)}/sheet-webhook`,
        { webhook_url: sheetHookUrl.trim() }
      );
      toast.success(
        sheetHookUrl.trim()
          ? `Webhook saved for ${refName}`
          : `Webhook cleared for ${refName}`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSheetHookSaving(false);
    }
  };
  const pushToSheet = async () => {
    if (!sheetHookUrl.trim()) {
      toast.error("Save the webhook URL first");
      return;
    }
    if (!window.confirm(
      `Push ${refName}'s ledger (${range.from} → ${range.to}) to Google Sheet?\n` +
      `Sheet will be CLEARED and rewritten fresh — no dupes.`
    )) return;
    setSheetHookPushing(true);
    try {
      const { data: res } = await api.post(
        `/wallet/ledger/staff/${encodeURIComponent(refName)}/push-to-sheet?from_=${range.from}&to=${range.to}`
      );
      toast.success(
        `Pushed ${res.rows_pushed} rows to sheet (HTTP ${res.webhook_status})`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSheetHookPushing(false);
    }
  };
  const copyAppsScript = async () => {
    try {
      await navigator.clipboard.writeText(APPS_SCRIPT_SNIPPET);
      toast.success("Apps Script copied — paste into your Google Sheet");
    } catch {
      toast.error("Copy failed — select manually below and copy");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/wallet/ledger/${scope}/${refId}?from_=${range.from}&to=${range.to}`
      );
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [scope, refId, range.from, range.to]);

  const [postingEntry, setPostingEntry] = useState(false);
  const addEntry = async () => {
    if (postingEntry) return;
    if (!entry.amount) {
      toast.error("Amount required");
      return;
    }
    setPostingEntry(true);
    // Client-generated nonce so a rapid double-tap / network retry
    // resolves to the same server row instead of creating duplicates.
    const tempId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    // Qty + Rate (optional) — only sent for adjustment kinds where the
    // UI exposes those inputs. Backend validates qty×rate ≈ amount.
    const qtyNum = parseFloat(preset.qty || "");
    const rateNum = parseFloat(preset.rate || "");
    const payload = {
      ref_id: refId,
      kind: entry.kind,
      amount: parseFloat(entry.amount),
      date: entry.date,
      note: entry.note,
      client_temp_id: tempId,
    };
    if (!Number.isNaN(qtyNum) && qtyNum > 0) payload.qty = qtyNum;
    if (!Number.isNaN(rateNum) && rateNum > 0) payload.rate = rateNum;
    try {
      await api.post(`/wallet/entries/${scope}`, payload);
      toast.success("Entry added");
      setEntry({ kind: "payment", amount: "", date: todayIST(), note: "" });
      setPreset({ reason: "", qty: "", rate: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPostingEntry(false);
    }
  };

  // Staff-Excel reconciliation — POST /api/wallet/ledger/staff/<name>/reconcile
  // with the uploaded xlsx as multipart. Backend returns row-wise diff +
  // summary counts which we render in the modal. Only enabled for staff
  // ledgers (scope === "staff") because the endpoint only handles staff.
  const runReconciliation = async () => {
    if (!reconFile) return;
    setReconLoading(true);
    setReconResult(null);
    try {
      const fd = new FormData();
      fd.append("file", reconFile);
      const { data: res } = await api.post(
        `/wallet/ledger/staff/${encodeURIComponent(refName)}/reconcile`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setReconResult(res);
      toast.success(
        `Reconciled: ${res.summary.matched} matched, ${res.summary.amount_mismatch} mismatch, ${res.summary.missing_in_our + res.summary.missing_in_staff} missing`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReconLoading(false);
    }
  };

  const downloadReconExcel = async () => {
    if (!reconResult) return;
    const ExcelJSMod = await import("exceljs");
    const ExcelJS = ExcelJSMod.default || ExcelJSMod;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Reconciliation", {
      views: [{ state: "frozen", ySplit: 6 }],
    });
    const title = ws.addRow([`STAFF LEDGER RECONCILIATION — ${refName}`]);
    title.font = { bold: true, size: 14, color: { argb: "FF385C4B" } };
    ws.mergeCells(`A${title.number}:F${title.number}`);
    ws.addRow([
      `Generated ${new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST`,
    ]);
    const s = reconResult.summary;
    ws.addRow([`Total rows: ${s.total}`]);
    ws.addRow([
      `  Matched: ${s.matched} · Amount mismatch: ${s.amount_mismatch} · Missing in our: ${s.missing_in_our} · Missing in staff: ${s.missing_in_staff}`,
    ]);
    ws.addRow([]);
    const header = ws.addRow([
      "Date",
      "Agency",
      "Our amount (₹)",
      "Staff amount (₹)",
      "Difference (₹)",
      "Status",
    ]);
    header.font = { bold: true, color: { argb: "FFFFFFFF" } };
    header.eachCell((c) => {
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF385C4B" } };
    });
    const colors = {
      matched: "FFA7F3D0",         // emerald-200
      amount_mismatch: "FFFECACA", // red-200
      missing_in_our: "FFFDE68A",  // amber-200
      missing_in_staff: "FFDDD6FE",// violet-200
    };
    for (const r of reconResult.rows) {
      const row = ws.addRow([
        r.date,
        r.agency_display || r.agency_key,
        r.our_amount,
        r.staff_amount,
        r.diff,
        r.status.replace(/_/g, " ").toUpperCase(),
      ]);
      const fill = colors[r.status] || "FFFFFFFF";
      row.eachCell((c) => {
        c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
      });
    }
    ws.columns.forEach((col) => {
      let max = 10;
      col.eachCell({ includeEmpty: false }, (c) => {
        const l = String(c.value ?? "").length;
        if (l > max) max = l;
      });
      col.width = Math.min(max + 2, 40);
    });
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reconciliation_${refName.replace(/\s+/g, "_")}_${todayIST()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Reconciliation Excel downloaded");
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-2 sm:p-6"
      onClick={onClose}
      data-testid="ledger-drawer"
    >
      <div
        className="bg-[#F8F8F3] w-full max-w-5xl max-h-[92vh] rounded-lg border border-[#EDEDE5] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-[#EDEDE5]">
          <div className="flex items-center gap-2">
            {scope === "agency" ? (
              <Building2 className="w-4 h-4 text-[#385C4B]" />
            ) : (
              <UserCircle2 className="w-4 h-4 text-[#385C4B]" />
            )}
            <h3
              className="font-semibold text-[#1C1C19]"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="ledger-title"
            >
              {refName} — {scope === "agency" ? "Agency Ledger" : "Staff Ledger"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-[#F0F0EA]"
            data-testid="ledger-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#385C4B]" />
          </div>
        )}

        {data && !loading && (
          <div className="overflow-y-auto flex-1 p-4 space-y-4">
            <DateRangeChips
              from={range.from}
              to={range.to}
              onChange={setRange}
              testidPrefix="admin-ledger-date"
            />
            {/* Totals bar */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              {[
                ["Opening", data.totals.opening],
                [
                  scope === "agency" ? "Deliveries" : "Earnings",
                  scope === "agency"
                    ? data.totals.delivery_earnings
                    : data.totals.earnings,
                ],
                ["Adjustments", data.totals.adjustments],
                [scope === "agency" ? "Paid" : "Payouts", -(scope === "agency" ? data.totals.payments_received : data.totals.payouts)],
                ["Balance", data.totals.balance],
              ].map(([label, val], i) => (
                <div
                  key={label}
                  className="bg-white border border-[#EDEDE5] rounded px-3 py-2"
                >
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B61]">
                    {label}
                  </div>
                  <div
                    className={`font-mono font-semibold text-base ${
                      label === "Balance"
                        ? val > 0
                          ? "text-red-700"
                          : val < 0
                          ? "text-emerald-700"
                          : ""
                        : ""
                    }`}
                    data-testid={`ledger-total-${label.toLowerCase()}`}
                  >
                    {rupee(val)}
                  </div>
                </div>
              ))}
            </div>

            {/* Download bar */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setSortOrder((v) => (v === "desc" ? "asc" : "desc"))
                }
                title={
                  sortOrder === "desc"
                    ? "Abhi Newest first — click for bank passbook mode"
                    : "Abhi Oldest first (passbook) — click for Newest first"
                }
                data-testid="ledger-sort-toggle"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#385C4B]/40 text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white transition-colors"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6L4 10M4 10L8 14M4 10H16M16 18L20 14M20 14L16 10M20 14H8" /></svg>
                Sort: {sortOrder === "desc" ? "Newest first" : "Oldest first"}
              </button>
              <button
                onClick={async (ev) => {
                  // Iter74: fresh-fetch parity with Download PDF — CSV
                  // must always reflect the current range, not a stale
                  // load() from before a preset change.
                  const btn = ev.currentTarget;
                  btn.setAttribute("data-loading", "1");
                  try {
                    const { data: fresh } = await api.get(
                      `/wallet/ledger/${scope}/${refId}?from_=${range.from}&to=${range.to}`
                    );
                    setData(fresh);
                    const csv =
                      scope === "agency"
                        ? buildAgencyLedgerCSV(fresh, sortOrder)
                        : buildStaffLedgerCSV(fresh, sortOrder);
                    downloadCSV(
                      `${scope}Ledger_${refName.replace(/\s+/g, "_")}_${fresh.to_date}.csv`,
                      csv
                    );
                  } catch (e) {
                    toast.error(formatApiError(e) || "CSV download failed");
                  } finally {
                    btn.removeAttribute("data-loading");
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#385C4B]/40 text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white transition-colors data-[loading=1]:opacity-60"
                data-testid="ledger-download-csv"
              >
                <Download className="w-3.5 h-3.5" /> Download Excel (CSV)
              </button>
              <button
                onClick={async (ev) => {
                  // Iter74: fetch a FRESH ledger snapshot for the current
                  // range right before generating the PDF. Prevents a race
                  // where the range was changed but the last successful
                  // load() is still on-screen, causing the downloaded PDF
                  // to encode a stale window/totals.
                  const btn = ev.currentTarget;
                  btn.setAttribute("data-loading", "1");
                  try {
                    const { data: fresh } = await api.get(
                      `/wallet/ledger/${scope}/${refId}?from_=${range.from}&to=${range.to}`
                    );
                    // Sync local state so on-screen totals match the PDF.
                    setData(fresh);
                    downloadLedgerPDF({
                      title:
                        (scope === "agency" ? "Agency" : "Staff") +
                        ` Ledger — ${refName}`,
                      subtitle: `Window ${fresh.from_date} to ${fresh.to_date} · Generated ${new Date().toLocaleDateString("en-IN")}`,
                      ledger: fresh,
                      isAgency: scope === "agency",
                      filename: `${scope}Ledger_${refName.replace(/\s+/g, "_")}_${fresh.to_date}.pdf`,
                      sortOrder,
                    });
                  } catch (e) {
                    toast.error(formatApiError(e) || "PDF download failed");
                  } finally {
                    btn.removeAttribute("data-loading");
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#385C4B]/40 text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white transition-colors data-[loading=1]:opacity-60"
                data-testid="ledger-download-pdf"
              >
                <FileDown className="w-3.5 h-3.5" /> Download PDF
              </button>
              {scope === "staff" && (
                <button
                  onClick={async (ev) => {
                    const n = (data?.rows || []).filter(r => r.kind === "earning" && (!r.rate || r.rate === 0)).length;
                    const promptMsg = n > 0
                      ? `${refName} ke ${n} entries me rate 0 hai (Mobile+Code / unclassified). Group name se operator infer karke fix karu?`
                      : `${refName} ke sabhi entries scan karke unclassified operator entries me stored operator field set karu (safe, idempotent).`;
                    if (!window.confirm(promptMsg)) return;
                    const btn = ev.currentTarget;
                    btn.setAttribute("data-loading", "1");
                    try {
                      const { data: res } = await api.post(
                        `/wallet/ledger/staff/${encodeURIComponent(refName)}/fix-missing-rates`
                      );
                      const by = res.patched_by_operator || {};
                      const parts = [];
                      if (by.INDANE) parts.push(`Indane: ${by.INDANE}`);
                      if (by.BHARAT_GAS) parts.push(`Bharat: ${by.BHARAT_GAS}`);
                      if (by.HP_GAS) parts.push(`HP: ${by.HP_GAS}`);
                      const summary = parts.length ? parts.join(" · ") : "koi entry patch nahi hui (sab already classified)";
                      toast.success(`${res.total_patched_entries} entries fixed · ${summary}`);
                      btn.setAttribute("data-copied", "1");
                      // Refresh the ledger so the corrected amounts show immediately
                      await load();
                    } catch (e) {
                      btn.setAttribute("data-copy-failed", "1");
                      toast.error(formatApiError(e) || "Fix Missing Rates failed");
                    } finally {
                      setTimeout(() => {
                        btn.removeAttribute("data-loading");
                        btn.removeAttribute("data-copied");
                        btn.removeAttribute("data-copy-failed");
                      }, 1800);
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#B45309]/40 text-[#B45309] rounded hover:bg-[#B45309] hover:text-white transition-colors data-[loading=1]:opacity-60 data-[copied=1]:bg-emerald-100 data-[copied=1]:border-emerald-500 data-[copied=1]:text-emerald-800 data-[copy-failed=1]:bg-red-100 data-[copy-failed=1]:text-red-700"
                  data-testid="ledger-fix-missing-rates"
                  title="10-digit Mobile+Code aur unclassified entries me operator infer karke fix karo — Indane 190 / Bharat 170 rate resolve ho jaega. Safe + idempotent."
                >
                  <Scale className="w-3.5 h-3.5" /> Fix Missing Rates
                </button>
              )}
              {scope === "staff" && (
                <button
                  onClick={() => setReconOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#B45309]/40 text-[#B45309] rounded hover:bg-[#B45309] hover:text-white transition-colors"
                  data-testid="ledger-open-reconcile"
                  title="Upload the staff's own Excel and see which date+agency amounts don't match our ledger"
                >
                  <Scale className="w-3.5 h-3.5" /> Reconcile with Staff Excel
                </button>
              )}
              {scope === "staff" && (
                <button
                  onClick={openSheetHookPanel}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#0F9D58]/50 text-[#0F9D58] rounded hover:bg-[#0F9D58] hover:text-white transition-colors"
                  data-testid="ledger-open-sheet-hook"
                  title={`Push ${refName}'s ledger to their OWN Google Sheet (Ashish/Komal ka mix nahi hoga)`}
                >
                  <Send className="w-3.5 h-3.5" /> Google Sheet
                </button>
              )}
              <span className="text-[11px] text-[#6B6B61] ml-auto">
                {data.rows.length} entries in window
              </span>
            </div>

            {/* Manual entry form */}
            <div className="bg-white border border-[#EDEDE5] rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B61] mb-2">
                Add manual entry
              </div>
              <div
                className="grid grid-cols-[130px_120px_140px_1fr_auto] gap-2"
                data-testid="ledger-entry-form"
              >
                <select
                  value={entry.kind}
                  onChange={(e) => setEntry({ ...entry, kind: e.target.value })}
                  className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded bg-white"
                  data-testid="ledger-entry-kind"
                >
                  <option value="opening">Opening Balance</option>
                  <option value="payment">
                    {scope === "agency" ? "Payment Received" : "Payout Made"}
                  </option>
                  <option value="adjustment">Adjustment (+)</option>
                  <option value="adjustment_minus">Adjustment (−)</option>
                </select>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Amount"
                  value={entry.amount}
                  onChange={(e) => setEntry({ ...entry, amount: e.target.value })}
                  className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded font-mono"
                  data-testid="ledger-entry-amount"
                />
                <input
                  type="date"
                  value={entry.date}
                  onChange={(e) => setEntry({ ...entry, date: e.target.value })}
                  className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded"
                />
                <input
                  type="text"
                  placeholder="Note (e.g. 'Cash Rs 5000 rcvd 03-Jul')"
                  value={entry.note}
                  onChange={(e) => setEntry({ ...entry, note: e.target.value })}
                  className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded"
                />
                <button
                  onClick={addEntry}
                  disabled={postingEntry}
                  className="px-3 py-1.5 bg-[#385C4B] text-white rounded text-sm hover:bg-[#2D4A3C] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  data-testid="ledger-entry-add"
                >
                  {postingEntry ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Plus className="w-3 h-3" />
                  )}
                  {postingEntry ? "Posting…" : "Post"}
                </button>
              </div>

              {/* Preset-reason helper — Adjustment kinds ke liye rate × qty
                  fields taaki har ledger change ka SAAF reason + math
                  trail dikhe. Rules same rahenge, sirf transparency add
                  ho rahi hai. */}
              {(entry.kind === "adjustment" || entry.kind === "adjustment_minus") && (
                <div
                  className="mt-2 pt-2 border-t border-[#EDEDE5] grid grid-cols-[130px_1fr_90px_90px_auto] gap-2 items-center"
                  data-testid="ledger-preset-row"
                >
                  <span className="text-[10px] uppercase tracking-wider text-[#6B6B61]">
                    Reason preset
                  </span>
                  <select
                    value={preset.reason}
                    onChange={(e) =>
                      applyPreset({ ...preset, reason: e.target.value })
                    }
                    className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded bg-white"
                    data-testid="ledger-preset-reason"
                  >
                    <option value="">— pick a reason (optional) —</option>
                    {PRESET_REASONS.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="Qty"
                    value={preset.qty}
                    onChange={(e) =>
                      applyPreset({ ...preset, qty: e.target.value })
                    }
                    className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded font-mono"
                    data-testid="ledger-preset-qty"
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Rate"
                    value={preset.rate}
                    onChange={(e) =>
                      applyPreset({ ...preset, rate: e.target.value })
                    }
                    className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded font-mono"
                    data-testid="ledger-preset-rate"
                  />
                  <span
                    className="text-xs font-mono text-[#385C4B] whitespace-nowrap"
                    data-testid="ledger-preset-computed"
                  >
                    {preset.qty && preset.rate
                      ? `= ₹${(+preset.qty * +preset.rate).toLocaleString("en-IN")}`
                      : ""}
                  </span>
                </div>
              )}
            </div>

            {/* Aggregated daily ledger — clean financial view, no DAC clutter.
                DAC-level details remain visible in the chat / LPG report.
                Admin gets a per-row Delete option (armed via a toggle) for
                manual entries in both agency and staff scopes. */}
            <AggregatedLedgerTable
              rows={data.rows}
              isAgency={scope === "agency"}
              agencyFallback={scope === "agency" ? refName : ""}
              sortOrder={sortOrder}
              currentBalance={data.totals?.balance ?? null}
              onDeleteManual={async (id) => {
                try {
                  await api.delete(`/wallet/entries/${id}`);
                  toast.success("Entry removed");
                  load();
                } catch (e) {
                  toast.error(formatApiError(e));
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Staff-Excel Reconciliation Modal — only reachable via the
          "⚖ Reconcile with Staff Excel" button in the ledger header when
          scope === "staff". Renders on top of the ledger drawer. */}
      {sheetHookOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3"
          onClick={(e) => {
            if (e.target === e.currentTarget && !sheetHookPushing && !sheetHookSaving) {
              setSheetHookOpen(false);
            }
          }}
          data-testid="ledger-sheet-hook-modal"
        >
          <div
            className="bg-[#FAFAF5] w-full max-w-2xl rounded-lg shadow-2xl border border-[#EDEDE5] max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-[#EDEDE5] flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Send className="w-4 h-4 text-[#0F9D58]" />
                {refName} — Google Sheet Push
              </div>
              <button
                onClick={() => !sheetHookPushing && setSheetHookOpen(false)}
                className="p-1 hover:bg-[#EDEDE5] rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4 space-y-4 text-sm">
              {sheetHookLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin text-[#385C4B]" />
                </div>
              ) : (
                <>
                  {/* Step 1 — Apps Script snippet */}
                  <div className="bg-white border border-[#EDEDE5] rounded p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-[#385C4B]">
                        1 · Google Sheet setup (one-time per staff)
                      </div>
                      <button
                        onClick={copyAppsScript}
                        className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border border-[#385C4B]/40 text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white transition-colors"
                        data-testid="ledger-sheet-copy-script"
                      >
                        <Copy className="w-3 h-3" /> Copy Apps Script
                      </button>
                    </div>
                    <ol className="list-decimal list-inside text-[12px] space-y-0.5 text-[#2A2A22]">
                      <li>Naya Google Sheet banao (sirf is staff ke liye)</li>
                      <li><b>Extensions → Apps Script</b> kholo</li>
                      <li>Purana code delete karke <b>Copy Apps Script</b> ka code paste karo</li>
                      <li><b>Deploy → New deployment → Web app</b> · <em>Execute as: Me</em> · <em>Who has access: Anyone</em></li>
                      <li>Deployed URL copy karke niche paste karo</li>
                    </ol>
                    <pre className="bg-[#1E1E1E] text-[#D4D4D4] text-[10px] leading-tight font-mono p-2 rounded overflow-x-auto max-h-40">
{APPS_SCRIPT_SNIPPET}
                    </pre>
                  </div>

                  {/* Step 2 — Webhook URL */}
                  <div className="bg-white border border-[#EDEDE5] rounded p-3 space-y-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-[#385C4B]">
                      2 · Webhook URL for {refName}
                    </div>
                    <input
                      type="url"
                      placeholder="https://script.google.com/macros/s/…/exec"
                      value={sheetHookUrl}
                      onChange={(e) => setSheetHookUrl(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-[#DCDCD3] rounded font-mono"
                      data-testid="ledger-sheet-hook-url"
                    />
                    {(sheetHookMeta.updated_at || sheetHookMeta.updated_by) && (
                      <div className="text-[10px] text-[#6B6B61] font-mono">
                        Last updated:{" "}
                        {sheetHookMeta.updated_at
                          ? new Date(sheetHookMeta.updated_at).toLocaleString("en-IN", {
                              timeZone: "Asia/Kolkata",
                            })
                          : "—"}
                        {sheetHookMeta.updated_by ? ` by ${sheetHookMeta.updated_by}` : ""}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={saveSheetHook}
                        disabled={sheetHookSaving}
                        className="px-3 py-1.5 text-xs font-semibold bg-[#385C4B] text-white rounded hover:bg-[#2A4638] disabled:opacity-50"
                        data-testid="ledger-sheet-hook-save"
                      >
                        {sheetHookSaving ? (
                          <><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Saving…</>
                        ) : (
                          <>Save Webhook</>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Step 3 — Push */}
                  <div className="bg-white border border-[#EDEDE5] rounded p-3 space-y-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-[#385C4B]">
                      3 · Push current window
                    </div>
                    <div className="text-[12px] text-[#2A2A22]">
                      Date range abhi: <b>{range.from}</b> → <b>{range.to}</b>
                      <br />
                      <span className="text-[#6B6B61]">
                        (Modal band karke <em>Today / Yesterday / Custom</em> chip se
                        badal sakte ho — phir dobara open karke Push karo)
                      </span>
                    </div>
                    <button
                      onClick={pushToSheet}
                      disabled={sheetHookPushing || !sheetHookUrl.trim()}
                      className="px-4 py-2 text-sm font-semibold bg-[#0F9D58] text-white rounded hover:bg-[#0B8043] disabled:opacity-50 flex items-center gap-2"
                      data-testid="ledger-sheet-hook-push"
                    >
                      {sheetHookPushing ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Pushing…</>
                      ) : (
                        <><Send className="w-4 h-4" /> Push to Google Sheet</>
                      )}
                    </button>
                    <div className="text-[10px] text-[#6B6B61]">
                      Sheet CLEAR karke fresh rows likhega — no duplicates.
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {reconOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3"
          onClick={(e) => {
            if (e.target === e.currentTarget && !reconLoading) {
              setReconOpen(false);
            }
          }}
          data-testid="ledger-reconcile-modal"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#EDEDE5]">
              <div className="flex items-center gap-2">
                <Scale className="w-4 h-4 text-[#B45309]" />
                <h4 className="font-semibold text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Reconcile — {refName}&apos;s Excel vs our ledger
                </h4>
              </div>
              <button
                onClick={() => !reconLoading && setReconOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-[#F0F0EA]"
                data-testid="ledger-reconcile-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              <div className="bg-[#FFF8E7] border border-[#B45309]/25 rounded p-3 text-[12px] text-[#7C4A03] leading-relaxed">
                <b>Kaise chalta hai:</b> Staff ki apni Excel (Ashish jaisi format — har date ek sheet, agency + amount) upload karo.
                System dono side ka comparison karega — <b>date + agency</b> match karke amount check karega. Indane 190/lpg, Bharat/HP 170/lpg
                rate se auto calculate hoga. Missing entries + amount mismatches highlight hongi.
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 px-3 py-2 border border-[#DCDCD3] rounded cursor-pointer hover:bg-[#F5F5EE] text-sm">
                  <Upload className="w-4 h-4 text-[#385C4B]" />
                  <span>{reconFile ? reconFile.name : "Choose Staff Excel (.xlsx)"}</span>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setReconFile(f);
                        setReconResult(null);
                      }
                    }}
                    data-testid="ledger-reconcile-file-input"
                  />
                </label>
                <button
                  disabled={!reconFile || reconLoading}
                  onClick={runReconciliation}
                  className="px-4 py-2 bg-[#B45309] text-white rounded text-sm font-semibold hover:bg-[#8B3E07] disabled:opacity-50 flex items-center gap-2"
                  data-testid="ledger-reconcile-run"
                >
                  {reconLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
                  {reconLoading ? "Comparing..." : "Compare"}
                </button>
                {reconResult && (
                  <button
                    onClick={downloadReconExcel}
                    className="px-4 py-2 bg-[#385C4B] text-white rounded text-sm font-semibold hover:bg-[#2D4A3C] flex items-center gap-2"
                    data-testid="ledger-reconcile-download"
                  >
                    <Download className="w-4 h-4" />
                    Download Excel Report
                  </button>
                )}
                <a
                  href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/wallet/ledger/staff/reconcile/template.xlsx`}
                  onClick={(e) => {
                    // Attach the bearer token via fetch → blob → download
                    // because <a href> can't send auth headers. This
                    // preserves the require_admin guard on the endpoint.
                    e.preventDefault();
                    (async () => {
                      try {
                        const { data } = await api.get(
                          "/wallet/ledger/staff/reconcile/template.xlsx",
                          { responseType: "blob" }
                        );
                        const url = URL.createObjectURL(data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = "staff_ledger_reconciliation_template.xlsx";
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                        toast.success("Template downloaded — share with staff");
                      } catch (err) {
                        toast.error(formatApiError(err));
                      }
                    })();
                  }}
                  className="px-3 py-2 border border-[#4C1D95]/40 text-[#4C1D95] text-sm font-semibold rounded hover:bg-[#4C1D95] hover:text-white flex items-center gap-2 cursor-pointer"
                  data-testid="ledger-reconcile-template"
                  title="Perfect-match format template staff ke liye — bas download karke share karein"
                >
                  <FileDown className="w-4 h-4" />
                  Download Format Template
                </a>
              </div>

              {reconResult && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      ["Matched", reconResult.summary.matched, "bg-emerald-100 text-emerald-800 border-emerald-300"],
                      ["Amount Mismatch", reconResult.summary.amount_mismatch, "bg-red-100 text-red-800 border-red-300"],
                      ["Missing in Our", reconResult.summary.missing_in_our, "bg-amber-100 text-amber-800 border-amber-300"],
                      ["Missing in Staff", reconResult.summary.missing_in_staff, "bg-violet-100 text-violet-800 border-violet-300"],
                    ].map(([label, val, cls], i) => (
                      <div
                        key={label}
                        className={`px-3 py-2 rounded border ${cls}`}
                        data-testid={`ledger-reconcile-summary-${String(label).toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
                        <div className="font-mono font-bold text-lg">{val}</div>
                      </div>
                    ))}
                  </div>

                  <div className="border border-[#EDEDE5] rounded overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F5F5EE] text-[#6B6B61] text-[11px] uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-3 py-2">Date</th>
                          <th className="text-left px-3 py-2">Agency</th>
                          <th className="text-right px-3 py-2">Our (₹)</th>
                          <th className="text-right px-3 py-2">Staff (₹)</th>
                          <th className="text-right px-3 py-2">Diff (₹)</th>
                          <th className="text-left px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reconResult.rows.map((r, i) => {
                          const rowClass = {
                            matched: "bg-emerald-50",
                            amount_mismatch: "bg-red-50",
                            missing_in_our: "bg-amber-50",
                            missing_in_staff: "bg-violet-50",
                          }[r.status] || "";
                          const badgeClass = {
                            matched: "bg-emerald-500 text-white",
                            amount_mismatch: "bg-red-500 text-white",
                            missing_in_our: "bg-amber-500 text-white",
                            missing_in_staff: "bg-violet-500 text-white",
                          }[r.status] || "bg-slate-500 text-white";
                          return (
                            <tr
                              key={`${r.date}-${r.agency_key || r.agency_display || ""}-${r.status}-${i}`}
                              className={`border-t border-[#EDEDE5] ${rowClass}`}
                              data-testid={`ledger-reconcile-row-${i}`}
                            >
                              <td className="px-3 py-1.5 font-mono text-xs">{r.date}</td>
                              <td className="px-3 py-1.5">{r.agency_display || r.agency_key}</td>
                              <td className="px-3 py-1.5 font-mono text-right">₹{Math.round(r.our_amount).toLocaleString("en-IN")}</td>
                              <td className="px-3 py-1.5 font-mono text-right">₹{Math.round(r.staff_amount).toLocaleString("en-IN")}</td>
                              <td className={`px-3 py-1.5 font-mono text-right font-semibold ${r.diff !== 0 ? "text-red-700" : "text-slate-500"}`}>
                                {r.diff !== 0 ? `₹${Math.round(r.diff).toLocaleString("en-IN")}` : "—"}
                              </td>
                              <td className="px-3 py-1.5">
                                <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${badgeClass}`}>
                                  {r.status.replace(/_/g, " ")}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        {reconResult.rows.length === 0 && (
                          <tr>
                            <td colSpan={6} className="px-3 py-6 text-center text-[#6B6B61] italic">
                              Koi data compare karne ke liye nahi mila.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {!reconResult && !reconLoading && (
                <div className="text-[12px] text-[#6B6B61] italic pt-6 pb-4 text-center">
                  Upload the staff&apos;s Excel above and hit <b>Compare</b> to see the side-by-side diff.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// -----------------------------------------------------------------------------
function DailySummaryTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  // Dedup dialog state — shows dry-run result of cleaning duplicate
  // lpg_entries for a single (group_id, date) pair, then admin confirms.
  const [dedupModal, setDedupModal] = useState(null); // {group_id, date, agency_name, dryResult, loading, executing}
  const openDedup = async (row) => {
    setDedupModal({
      group_id: row.group_id,
      date: row.date,
      agency_name: row.agency_name,
      dryResult: null,
      loading: true,
      executing: false,
    });
    try {
      const { data: dry } = await api.post("/admin/wallet/dedup-agency-day", {
        group_id: row.group_id,
        date: row.date,
        dry_run: true,
      });
      setDedupModal((m) => (m ? { ...m, dryResult: dry, loading: false } : null));
    } catch (e) {
      toast.error(formatApiError(e));
      setDedupModal(null);
    }
  };
  const confirmDedup = async () => {
    if (!dedupModal) return;
    setDedupModal((m) => ({ ...m, executing: true }));
    try {
      const { data: live } = await api.post("/admin/wallet/dedup-agency-day", {
        group_id: dedupModal.group_id,
        date: dedupModal.date,
        dry_run: false,
      });
      toast.success(`Removed ${live.entries_removed} duplicate LPG entries`);
      setDedupModal(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setDedupModal((m) => (m ? { ...m, executing: false } : null));
    }
  };
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/wallet/matrix?days=${days}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [days]);
  const rupee = (n) =>
    "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (s) => {
    if (!s) return "";
    const [y, m, d] = s.split("-");
    return new Date(Date.UTC(+y, +m - 1, +d)).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };
  const totals = useMemo(() => {
    if (!data) return null;
    return data.rows.reduce(
      (acc, r) => ({
        qty: acc.qty + (r.qty || 0),
        amount: acc.amount + (r.amount || 0),
        received: acc.received + (r.received || 0),
      }),
      { qty: 0, amount: 0, received: 0 }
    );
  }, [data]);
  const downloadCSV = () => {
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      "XYZ CHATTING — DAILY FINANCIAL SUMMARY",
      `Window: ${data.from_date} → ${data.to_date}`,
      "",
      ["Date", "Agency", "Qty", "Rate", "Amount (Qty × Rate)", "Received", "Pending", "Advance"].join(","),
    ];
    for (const r of data.rows) {
      lines.push(
        [fmtDate(r.date), esc(r.agency_name), r.qty, r.rate, r.amount, r.received, r.pending, r.advance].join(",")
      );
    }
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `DailyFinancialSummary_${data.to_date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Portrait A4 PDF of the same matrix — money is formatted as `Rs.` (not `₹`)
  // to avoid missing-glyph tofu boxes in jsPDF's default Helvetica font.
  const downloadPDF = () => {
    if (!data) return;
    const money = (n) =>
      "Rs. " +
      Number(n || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const marginX = 32;

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(28, 28, 25);
    doc.text("XYZ CHATTING — Daily Financial Summary", marginX, 44);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(107, 107, 97);
    doc.text(`Window: ${data.from_date}  →  ${data.to_date}`, marginX, 62);

    // Totals strip — 3 cards (Qty · Amount · Received)
    const cards = [
      ["Total Qty", String(totals?.qty || 0)],
      ["Amount", money(totals?.amount || 0)],
      ["Received", money(totals?.received || 0)],
    ];
    const stripY = 78;
    const stripH = 42;
    const gap = 8;
    const cardW = (pageW - marginX * 2 - gap * 2) / 3;
    for (let i = 0; i < cards.length; i++) {
      const x = marginX + i * (cardW + gap);
      doc.setDrawColor(237, 237, 229);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(x, stripY, cardW, stripH, 4, 4, "FD");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(107, 107, 97);
      doc.text(cards[i][0].toUpperCase(), x + 8, stripY + 14);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(28, 28, 25);
      doc.text(cards[i][1], x + 8, stripY + 30);
    }

    // Matrix table
    autoTable(doc, {
      startY: stripY + stripH + 12,
      head: [["Date", "Agency", "Qty", "Rate", "Amount", "Received", "Pending", "Advance"]],
      body: (data.rows || []).map((r) => [
        fmtDate(r.date),
        r.agency_name,
        r.qty,
        r.rate ? money(r.rate) : "—",
        r.amount ? money(r.amount) : "—",
        r.received ? money(r.received) : "",
        r.pending ? money(r.pending) : "",
        r.advance ? money(r.advance) : "",
      ]),
      styles: { font: "helvetica", fontSize: 8, cellPadding: 4, textColor: [28, 28, 25] },
      headStyles: {
        fillColor: [56, 92, 75],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [248, 248, 243] },
      columnStyles: {
        0: { cellWidth: 60 },
        2: { halign: "right", cellWidth: 32 },
        3: { halign: "right", cellWidth: 55 },
        4: { halign: "right", cellWidth: 65 },
        5: { halign: "right", cellWidth: 65, textColor: [56, 92, 75] },
        6: { halign: "right", cellWidth: 65, textColor: [180, 60, 60] },
        7: { halign: "right", cellWidth: 65, textColor: [56, 92, 75] },
      },
      margin: { left: marginX, right: marginX },
    });

    // Footer with page numbers
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 140);
      doc.text(
        `Page ${i} of ${pages}   ·   generated ${new Date().toLocaleString("en-IN")}`,
        pageW / 2,
        doc.internal.pageSize.getHeight() - 16,
        { align: "center" },
      );
    }
    doc.save(`DailyFinancialSummary_${data.to_date}.pdf`);
  };
  return (
    <SectionCard
      title="Daily Financial Summary"
      icon={IndianRupee}
      right={
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-2 py-1 text-xs border border-[#DCDCD3] rounded bg-white"
            data-testid="summary-days-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={15}>Last 15 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 6 months</option>
            <option value={365}>Last 1 year</option>
          </select>
          <button
            onClick={downloadCSV}
            disabled={!data}
            data-testid="summary-download-csv"
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-white border border-[#385C4B]/40 text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white transition-colors disabled:opacity-40"
          >
            <Download className="w-3 h-3" /> CSV
          </button>
          <button
            onClick={downloadPDF}
            disabled={!data}
            data-testid="summary-download-pdf"
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-[#385C4B] text-white rounded hover:bg-[#2D4A3C] transition-colors disabled:opacity-40 whitespace-nowrap"
          >
            <FileDown className="w-3 h-3" /> PDF
          </button>
        </div>
      }
    >
      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-[#385C4B]" />
        </div>
      )}
      {!loading && data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="text-[10px] uppercase tracking-wider text-[#6B6B61] bg-[#F5F5F0]">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Agency</th>
                <th className="text-right px-3 py-2">Qty</th>
                <th className="text-right px-3 py-2">Rate</th>
                <th className="text-right px-3 py-2">Amount (× )</th>
                <th className="text-right px-3 py-2 text-emerald-700">Received</th>
                <th className="text-right px-3 py-2 text-red-700">Pending</th>
                <th className="text-right px-3 py-2 text-blue-700">Advance</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-6 text-[#6B6B61]">
                    No activity in this window.
                  </td>
                </tr>
              )}
              {data.rows.map((r, i) => (
                <tr
                  key={`${r.date}-${r.group_id}`}
                  className="border-t border-[#F0F0EA] hover:bg-[#F8F8F3]"
                  data-testid={`summary-row-${i}`}
                >
                  <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-3 py-1.5">{r.agency_name}</td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    <span className="inline-flex items-center gap-1.5">
                      {r.qty}
                      <button
                        onClick={() => openDedup(r)}
                        title="Clean duplicate LPG entries for this day"
                        data-testid={`dedup-btn-${i}`}
                        className="text-[#8A6046] hover:text-red-600 hover:bg-red-500/10 rounded px-1 py-0.5 text-[10px] transition"
                      >
                        🧹
                      </button>
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">{r.rate ? rupee(r.rate) : <span className="text-red-500 text-[10px]">not set</span>}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-semibold">{r.amount ? rupee(r.amount) : "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{r.received ? rupee(r.received) : ""}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-red-700 font-semibold">{r.pending > 0 ? rupee(r.pending) : ""}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-blue-700 font-semibold">{r.advance > 0 ? rupee(r.advance) : ""}</td>
                </tr>
              ))}
            </tbody>
            {totals && data.rows.length > 0 && (
              <tfoot className="bg-[#F5F5F0] border-t-2 border-[#385C4B]" data-testid="summary-total">
                <tr>
                  <td className="px-3 py-2 font-bold uppercase tracking-wider text-xs" colSpan={2}>
                    TOTAL
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{totals.qty}</td>
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2 text-right font-mono font-bold">{rupee(totals.amount)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{rupee(totals.received)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      {dedupModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="dedup-modal"
        >
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-[#DCDCD3] flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-[#1C1C19]">
                  🧹 Clean Duplicate LPG Entries
                </div>
                <div className="text-xs text-[#6B6B61]">
                  {dedupModal.agency_name} · {fmtDate(dedupModal.date)}
                </div>
              </div>
              <button
                onClick={() => setDedupModal(null)}
                className="text-[#6B6B61] hover:text-[#1C1C19]"
                data-testid="dedup-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {dedupModal.loading ? (
                <div className="flex items-center gap-2 text-[#6B6B61]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Scanning for duplicates…
                </div>
              ) : !dedupModal.dryResult ? null : dedupModal.dryResult.entries_removed === 0 ? (
                <div className="text-sm text-emerald-800 bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
                  <b>No duplicates found</b> — {dedupModal.agency_name}&#39;s {fmtDate(dedupModal.date)} data is already clean.
                </div>
              ) : (
                <>
                  <div className="text-sm text-amber-900 bg-amber-500/10 border border-amber-500/30 rounded p-3">
                    <b>{dedupModal.dryResult.entries_removed}</b> duplicate LPG entries detected across{" "}
                    <b>{dedupModal.dryResult.total_messages_scanned}</b> messages.
                    Clicking <b>Remove</b> will keep the earliest occurrence of each LPG ID and strip the rest.
                    Wrong-status entries (complaints) are always preserved.
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B61]">
                    Duplicates ({dedupModal.dryResult.duplicate_lpgs.length}):
                  </div>
                  <div className="max-h-60 overflow-y-auto text-xs font-mono border border-[#DCDCD3] rounded">
                    {dedupModal.dryResult.duplicate_lpgs.slice(0, 200).map((d, i) => (
                      <div key={`${d.lpg_id}-${d.date || i}`} className="px-2 py-1 border-b border-[#F0F0EA] last:border-b-0">
                        <span className="text-[#385C4B] font-semibold">{d.lpg_id}</span>
                        <span className="text-[#6B6B61] ml-2">
                          keep in msg {d.kept_msg_id.slice(0, 8)}… ·
                          remove from {d.removed_from_msg_ids.length} other msg
                        </span>
                      </div>
                    ))}
                    {dedupModal.dryResult.duplicate_lpgs.length > 200 && (
                      <div className="px-2 py-1 text-[#6B6B61]">
                        … {dedupModal.dryResult.duplicate_lpgs.length - 200} more
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="px-5 py-3 border-t border-[#DCDCD3] flex items-center justify-end gap-2">
              <button
                onClick={() => setDedupModal(null)}
                data-testid="dedup-cancel"
                className="px-3 py-1.5 text-sm border border-[#DCDCD3] rounded hover:bg-[#F5F5F0]"
              >
                Cancel
              </button>
              {dedupModal.dryResult && dedupModal.dryResult.entries_removed > 0 && (
                <button
                  onClick={confirmDedup}
                  disabled={dedupModal.executing}
                  data-testid="dedup-confirm"
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {dedupModal.executing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Remove {dedupModal.dryResult.entries_removed} duplicate{dedupModal.dryResult.entries_removed !== 1 ? "s" : ""}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// -----------------------------------------------------------------------------
// Summary tab (Agencies)
// -----------------------------------------------------------------------------
function AgencySummaryTab({ summary, onOpen }) {
  return (
    <SectionCard title="Agencies · Current Balances" icon={Building2}>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-[#6B6B61] bg-[#F5F5F0]">
          <tr>
            <th className="text-left px-3 py-2">Agency</th>
            <th className="text-right px-3 py-2 w-40">Balance</th>
            <th className="text-right px-3 py-2 w-32" />
          </tr>
        </thead>
        <tbody>
          {summary.length === 0 && (
            <tr>
              <td colSpan={3} className="text-center py-6 text-[#6B6B61]">
                No agencies.
              </td>
            </tr>
          )}
          {summary.map((a) => (
            <tr
              key={a.group_id}
              className="border-t border-[#F0F0EA] hover:bg-[#F8F8F3]"
              data-testid={`agency-summary-row-${a.group_id}`}
            >
              <td className="px-3 py-2 font-medium">{a.name}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                <BalancePill amount={a.balance} />
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button
                  onClick={() => onOpen("agency", a.group_id, a.name)}
                  className="text-[#385C4B] text-xs font-semibold hover:underline"
                  data-testid={`agency-open-ledger-${a.group_id}`}
                >
                  Open ledger →
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
}

function StaffSummaryTab({ summary, onOpen }) {
  return (
    <SectionCard title="Staff · Current Payables" icon={UserCircle2}>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-[#6B6B61] bg-[#F5F5F0]">
          <tr>
            <th className="text-left px-3 py-2">Staff</th>
            <th className="text-right px-3 py-2 w-24">Uploads</th>
            <th className="text-right px-3 py-2 w-40">Payable</th>
            <th className="text-right px-3 py-2 w-32" />
          </tr>
        </thead>
        <tbody>
          {summary.length === 0 && (
            <tr>
              <td colSpan={4} className="text-center py-6 text-[#6B6B61]">
                No staff activity.
              </td>
            </tr>
          )}
          {summary.map((s) => (
            <tr
              key={s.name}
              className="border-t border-[#F0F0EA] hover:bg-[#F8F8F3]"
              data-testid={`staff-summary-row-${s.name}`}
            >
              <td className="px-3 py-2 font-medium">{s.name}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{s.uploads}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                <BalancePill amount={s.balance} />
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button
                  onClick={() => onOpen("staff", s.name, s.name)}
                  className="text-[#385C4B] text-xs font-semibold hover:underline"
                  data-testid={`staff-open-ledger-${s.name}`}
                >
                  Open ledger →
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
}

// -----------------------------------------------------------------------------
// Settlements tab
// -----------------------------------------------------------------------------
function SettlementsTab({ agencies, staffList, settlements, reload }) {
  const [form, setForm] = useState({
    scope: "agency",
    ref_id: "",
    amount: "",
    note: "",
  });
  const create = async () => {
    if (!form.ref_id || !form.amount) {
      toast.error("Ref + amount required");
      return;
    }
    try {
      await api.post("/wallet/settlements", {
        scope: form.scope,
        ref_id: form.ref_id,
        amount: parseFloat(form.amount),
        note: form.note,
      });
      toast.success("Settlement request created");
      setForm({ scope: "agency", ref_id: "", amount: "", note: "" });
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const decide = async (id, action) => {
    try {
      await api.post(`/wallet/settlements/${id}/${action}`);
      toast.success(action === "approve" ? "Approved — payment posted" : "Rejected");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const nameOf = (s) => {
    if (s.scope === "agency") {
      const g = agencies.find((a) => a.id === s.ref_id);
      return g ? g.name : s.ref_id;
    }
    return s.ref_id;
  };

  return (
    <SectionCard title="Settlement Requests" icon={Inbox}>
      <div
        className="grid grid-cols-[110px_1fr_130px_1fr_auto] gap-2 mb-4"
        data-testid="settlement-form"
      >
        <select
          value={form.scope}
          onChange={(e) => setForm({ ...form, scope: e.target.value, ref_id: "" })}
          className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded bg-white"
        >
          <option value="agency">Agency</option>
          <option value="staff">Staff</option>
        </select>
        {form.scope === "agency" ? (
          <select
            value={form.ref_id}
            onChange={(e) => setForm({ ...form, ref_id: e.target.value })}
            className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded bg-white"
          >
            <option value="">— pick agency —</option>
            {agencies.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            list="settlement-staff"
            value={form.ref_id}
            onChange={(e) => setForm({ ...form, ref_id: e.target.value })}
            className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded"
            placeholder="Staff name"
          />
        )}
        <datalist id="settlement-staff">
          {staffList.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
        <input
          type="number"
          step="0.01"
          placeholder="Amount ₹"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded font-mono"
        />
        <input
          type="text"
          placeholder="Note (optional)"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="px-2 py-1.5 text-sm border border-[#DCDCD3] rounded"
        />
        <button
          onClick={create}
          className="px-3 py-1.5 bg-[#385C4B] text-white rounded text-sm hover:bg-[#2D4A3C]"
          data-testid="settlement-create"
        >
          Create
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-[#6B6B61] bg-[#F5F5F0]">
          <tr>
            <th className="text-left px-2 py-2">Scope</th>
            <th className="text-left px-2 py-2">Ref</th>
            <th className="text-right px-2 py-2">Amount</th>
            <th className="text-left px-2 py-2">Note</th>
            <th className="text-left px-2 py-2">Status</th>
            <th className="text-right px-2 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {settlements.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center py-6 text-[#6B6B61]">
                No settlement requests.
              </td>
            </tr>
          )}
          {settlements.map((s) => (
            <tr
              key={s.id}
              className="border-t border-[#F0F0EA]"
              data-testid={`settlement-row-${s.id}`}
            >
              <td className="px-2 py-2 capitalize">{s.scope}</td>
              <td className="px-2 py-2">{nameOf(s)}</td>
              <td className="px-2 py-2 text-right font-mono">{rupee(s.amount)}</td>
              <td className="px-2 py-2 text-xs">{s.note || "—"}</td>
              <td className="px-2 py-2">
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                    s.status === "pending"
                      ? "bg-amber-100 text-amber-800"
                      : s.status === "approved"
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {s.status}
                </span>
              </td>
              <td className="px-2 py-2 text-right">
                {s.status === "pending" && (
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => decide(s.id, "approve")}
                      className="p-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                      title="Approve — posts payment entry"
                      data-testid={`settlement-approve-${s.id}`}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => decide(s.id, "reject")}
                      className="p-1 rounded bg-red-100 text-red-800 hover:bg-red-200"
                      title="Reject"
                      data-testid={`settlement-reject-${s.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
}

// -----------------------------------------------------------------------------
// Collect Amount tab (Admin only) — agency-wise current outstanding balance
// with WhatsApp-friendly copy and Excel export.
// -----------------------------------------------------------------------------
function CollectAmountTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/collect-amount-list");
      setRows(r.data.agencies || []);
      setTotal(r.data.grand_total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const copyText = async () => {
    if (rows.length === 0) {
      toast.error("Nothing to copy");
      return;
    }
    const lines = ["*Collect Amount List*", ""];
    rows.forEach((a) => {
      lines.push(`${a.agency_name} — ${rupee(a.balance)}`);
    });
    lines.push("─────────────");
    lines.push(`*Total: ${rupee(total)}*`);
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadExcel = async () => {
    if (rows.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    try {
      const ExcelJSMod = await import("exceljs");
      const ExcelJS = ExcelJSMod.default || ExcelJSMod;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Collect Amount");
      const header = ws.addRow(["Agency", "Balance (₹)"]);
      header.eachCell((c) => {
        c.font = { bold: true, color: { argb: "FFFFFFFF" } };
        c.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF385C4B" },
        };
        c.alignment = { horizontal: "center" };
      });
      rows.forEach((a) => {
        const r = ws.addRow([a.agency_name, Number(a.balance)]);
        r.getCell(2).numFmt = "#,##0.00";
      });
      const totalRow = ws.addRow(["TOTAL", Number(total)]);
      totalRow.eachCell((c) => {
        c.font = { bold: true };
        c.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEDEDE5" },
        };
      });
      totalRow.getCell(2).numFmt = "#,##0.00";
      ws.columns.forEach((col) => {
        let max = 12;
        col.eachCell({ includeEmpty: false }, (c) => {
          const l = String(c.value ?? "").length;
          if (l > max) max = l;
        });
        col.width = Math.min(max + 2, 40);
      });
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `collect_amount_${todayIST()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Excel export failed");
      console.error(e);
    }
  };

  return (
    <SectionCard
      title="Collect Amount · Agency-wise Pending"
      icon={IndianRupee}
      right={
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            disabled={loading}
            className="text-xs px-2 py-1 rounded border border-[#EDEDE5] hover:bg-[#F0F0EA] disabled:opacity-50"
            data-testid="collect-amount-refresh"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            onClick={copyText}
            className="text-xs px-2 py-1 rounded border border-[#385C4B] text-[#385C4B] hover:bg-[#EDF3F0] flex items-center gap-1"
            data-testid="collect-amount-copy"
          >
            <Copy className="w-3 h-3" />
            Copy
          </button>
          <button
            onClick={downloadExcel}
            className="text-xs px-2 py-1 rounded bg-[#385C4B] text-white hover:bg-[#2B4A3B] flex items-center gap-1"
            data-testid="collect-amount-excel"
          >
            <FileDown className="w-3 h-3" />
            Excel
          </button>
        </div>
      }
    >
      <table className="w-full text-sm" data-testid="collect-amount-table">
        <thead className="text-[10px] uppercase tracking-wider text-[#6B6B61] bg-[#F5F5F0]">
          <tr>
            <th className="text-left px-3 py-2">Agency</th>
            <th className="text-right px-3 py-2 w-48">Balance (₹)</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={2} className="text-center py-6 text-[#6B6B61]">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                Loading…
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={2} className="text-center py-8 text-[#6B6B61]">
                No pending amounts to collect. All agencies are settled.
              </td>
            </tr>
          )}
          {!loading &&
            rows.map((a) => (
              <tr
                key={a.agency_id}
                className="border-t border-[#F0F0EA] hover:bg-[#F8F8F3]"
                data-testid={`collect-amount-row-${a.agency_id}`}
              >
                <td className="px-3 py-2 font-medium">{a.agency_name}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums font-mono">
                  {rupee(a.balance)}
                </td>
              </tr>
            ))}
        </tbody>
        {!loading && rows.length > 0 && (
          <tfoot>
            <tr className="border-t-2 border-[#385C4B] bg-[#F8F8F3]">
              <td className="px-3 py-2 font-bold text-[#1C1C19]">
                Grand Total
              </td>
              <td
                className="px-3 py-2 text-right whitespace-nowrap tabular-nums font-mono font-bold text-[#385C4B]"
                data-testid="collect-amount-grand-total"
              >
                {rupee(total)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </SectionCard>
  );
}

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
export default function WalletPage({ embedded = false } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("summary");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ agencies: [], staff: [] });
  const [agencyRates, setAgencyRates] = useState([]);
  const [staffRates, setStaffRates] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [ledgerOpen, setLedgerOpen] = useState(null); // {scope, refId, refName}

  const load = async () => {
    setLoading(true);
    try {
      const [s, ar, sr, st] = await Promise.all([
        api.get("/wallet/summary"),
        api.get("/wallet/rates/agency"),
        api.get("/wallet/rates/staff"),
        api.get("/wallet/settlements"),
      ]);
      setSummary(s.data);
      setAgencyRates(ar.data.rates);
      setAgencies(ar.data.agencies);
      setStaffRates(sr.data.rates);
      setStaffList(sr.data.staff);
      setSettlements(st.data.settlements);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (user && user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#F8F8F3] flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[#1C1C19]">Wallet & Ledger</h1>
          <p className="text-[#6B6B61] mt-2">
            Admin-only area. Contact your Administrator for access.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: "summary", label: "Daily Summary", icon: IndianRupee },
    { key: "agencies", label: "Agencies", icon: Building2 },
    { key: "staff", label: "Staff", icon: UserCircle2 },
    { key: "collect", label: "Collect Amount", icon: Coins },
    { key: "rates", label: "Rate Master", icon: Coins },
    { key: "settlements", label: "Settlements", icon: Inbox },
    { key: "access", label: "Access", icon: Key },
  ];

  return (
    <div className={embedded ? "" : "min-h-screen bg-[#F8F8F3]"}>
      {!embedded && (
        <header className="border-b border-[#EDEDE5] bg-white sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/home")}
                className="w-9 h-9 rounded flex items-center justify-center hover:bg-[#F0F0EA]"
                data-testid="wallet-back"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#385C4B]" />
                <h1
                  className="font-semibold text-lg tracking-tight text-[#1C1C19]"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  Wallet & Ledger
                </h1>
                <span className="text-xs uppercase tracking-wider bg-[#385C4B] text-white px-2 py-0.5 rounded-full">
                  Admin
                </span>
              </div>
            </div>
          </div>
          {/* Tab strip */}
          <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto">
            {tabs.map((t) => {
              const Active = tab === t.key;
              const I = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 transition ${
                    Active
                      ? "border-[#385C4B] text-[#385C4B] font-semibold"
                      : "border-transparent text-[#6B6B61] hover:text-[#1C1C19]"
                  }`}
                  data-testid={`wallet-tab-${t.key}`}
                >
                  <I className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </header>
      )}

      {/* Embedded mode: render just the tab strip inline (no back button, no page-shell header) */}
      {embedded && (
        <div className="border-b border-[#EDEDE5] bg-white sticky top-0 z-10 -mx-4 px-4">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((t) => {
              const Active = tab === t.key;
              const I = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 transition ${
                    Active
                      ? "border-[#385C4B] text-[#385C4B] font-semibold"
                      : "border-transparent text-[#6B6B61] hover:text-[#1C1C19]"
                  }`}
                  data-testid={`wallet-tab-${t.key}`}
                >
                  <I className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <main className={embedded ? "pt-4" : "max-w-7xl mx-auto p-4"}>
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#385C4B]" />
          </div>
        )}
        {!loading && tab === "summary" && <DailySummaryTab />}
        {!loading && tab === "agencies" && (
          <AgencySummaryTab
            summary={summary.agencies}
            onOpen={(scope, id, name) => setLedgerOpen({ scope, refId: id, refName: name })}
          />
        )}
        {!loading && tab === "staff" && (
          <StaffSummaryTab
            summary={summary.staff}
            onOpen={(scope, id, name) => setLedgerOpen({ scope, refId: id, refName: name })}
          />
        )}
        {tab === "collect" && <CollectAmountTab />}
        {!loading && tab === "rates" && (
          <RateMasterTab
            agencies={agencies}
            staffList={staffList}
            agencyRates={agencyRates}
            staffRates={staffRates}
            reload={load}
          />
        )}
        {!loading && tab === "settlements" && (
          <SettlementsTab
            agencies={agencies}
            staffList={staffList}
            settlements={settlements}
            reload={load}
          />
        )}
        {!loading && tab === "access" && <LedgerAccessTab />}
      </main>

      {ledgerOpen && (
        <LedgerView
          scope={ledgerOpen.scope}
          refId={ledgerOpen.refId}
          refName={ledgerOpen.refName}
          onClose={() => {
            setLedgerOpen(null);
            load();
          }}
        />
      )}
    </div>
  );
}
